const DRAFT_PREFIX = 'coach.composer.draft.v1:';
const DRAFT_SAVE_DELAY_MS = 250;

type PendingDraft = {
  text: string;
  userId?: number;
  threadId?: string;
  timer: ReturnType<typeof setTimeout>;
};

const pendingDrafts = new Map<string, PendingDraft>();

export function coachDraftKey(userId?: number, threadId?: string): string {
  return `${DRAFT_PREFIX}${userId ?? 'anonymous'}:${threadId ?? 'current'}`;
}

export function loadCoachDraft(userId?: number, threadId?: string): string {
  try {
    return sessionStorage.getItem(coachDraftKey(userId, threadId)) ?? '';
  } catch {
    return '';
  }
}

export function persistCoachDraft(text: string, userId?: number, threadId?: string): void {
  try {
    const key = coachDraftKey(userId, threadId);
    if (text) sessionStorage.setItem(key, text);
    else sessionStorage.removeItem(key);
  } catch {
    return;
  }
}

export function scheduleCoachDraft(text: string, userId?: number, threadId?: string): void {
  const key = coachDraftKey(userId, threadId);
  const pending = pendingDrafts.get(key);
  if (pending) clearTimeout(pending.timer);

  const timer = setTimeout(() => {
    flushCoachDraft(userId, threadId);
  }, DRAFT_SAVE_DELAY_MS);
  pendingDrafts.set(key, { text, userId, threadId, timer });
}

export function flushCoachDraft(userId?: number, threadId?: string): void {
  const key = coachDraftKey(userId, threadId);
  const pending = pendingDrafts.get(key);
  if (!pending) return;

  clearTimeout(pending.timer);
  pendingDrafts.delete(key);
  persistCoachDraft(pending.text, pending.userId, pending.threadId);
}

export function clearCoachDrafts(userId?: number): void {
  const prefix = `${DRAFT_PREFIX}${userId ?? 'anonymous'}:`;
  for (const [key, pending] of pendingDrafts) {
    if (!key.startsWith(prefix)) continue;
    clearTimeout(pending.timer);
    pendingDrafts.delete(key);
  }
  try {
    for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
      const key = sessionStorage.key(index);
      if (key?.startsWith(prefix)) sessionStorage.removeItem(key);
    }
  } catch {
    return;
  }
}
